<script setup lang="ts">
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'reka-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/common/AppIcon.vue'
import FlagIcon from '@/components/common/FlagIcon.vue'
import { changeLanguage } from '@/services/session'
import { useLocalesStore } from '@/stores/locales'

/**
 * Selettore di lingua. Le voci sono le lingue **attive** a database, quindi
 * pubblicarne una nuova è un'operazione da pannello: qui non c'è nessun elenco
 * da tenere aggiornato.
 */
const props = withDefaults(
  defineProps<{
    /**
     * Which edge of the trigger the menu lines up with. `end` for a switcher at
     * the right of a header, `start` for one at the left (next to the logo),
     * so the menu always opens inwards instead of hanging off the screen.
     */
    align?: 'start' | 'end'
  }>(),
  { align: 'end' },
)

const { t, locale } = useI18n()
const locales = useLocalesStore()

const current = computed(() => locale.value)
const currentLanguage = computed(() => locales.languageFor(current.value))
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="chrome-item cursor-pointer gap-2 px-2"
      :aria-label="t('common.fields.language')"
    >
      <FlagIcon :locale="current" :src="currentLanguage?.flag_url" />
      <!-- Hidden in the md..lg band for the same reason as the username in
           UserMenu: that's where the row also carries the group/server
           selectors and clock (see AppHeader overflow bug). Hidden below sm
           too: with the Cinzel wordmark, hamburger + brand + this + the user
           menu came to ~370px on a 349px phone. The flag says it already. -->
      <span class="hidden text-xs font-semibold uppercase sm:inline md:hidden lg:inline">
        {{ current }}
      </span>
      <AppIcon name="mdi-chevron-down" class="text-base opacity-70" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side-offset="10"
        :align="props.align"
        class="panel z-50 min-w-40 p-1 shadow-raised"
      >
        <DropdownMenuItem
          v-for="option in locales.selectable"
          :key="option.iso_code"
          class="text-abyss-900 hover:bg-primary-50 data-highlighted:bg-primary-50 flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none"
          @select="changeLanguage(option.iso_code)"
        >
          <FlagIcon :locale="option.iso_code" :src="option.flag_url" />
          <!-- Il nome della lingua è un dato, non una stringa tradotta: sta a
               database, così una lingua nuova si presenta da sé. -->
          <span class="flex-1">{{ option.description }}</span>
          <AppIcon v-if="option.iso_code === current" name="mdi-check" class="text-primary-600" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
